import { CompassOutlined } from "@ant-design/icons";
import { App, Flex, Image as ImageComponent } from "antd";
import { FC, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Coords, Point, TaskSetGeoLocation } from "tm-shared/types";

import { FALLBACK_IMAGE, ROUTES } from "@/constants";
import { SVScanButton } from "@/features/SupervisorScan";
import { useAppDispatch } from "@/hooks/redux";
import {
    makeCommand,
    useMakeCommand,
} from "@/hooks/requestQueue/useRequestQueue";
import { Loading } from "@/shared/feedback/Loading";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import BaseButton from "@/shared/ui/BaseButton";
import { PrimaryText } from "@/shared/ui/Typography";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { setTaskCompleted } from "@/store/slices/work";
import { AnyQueueCommand } from "@/types/requestQueue";
import { getFileUrl, getPreciseLocation } from "@/utils";

import { StyledImageWrapper } from "./Styled";

interface SendGeoPageProps {
    task: TaskSetGeoLocation;
    point: Point;
}

export const SendGeoPage: FC<SendGeoPageProps> = ({ task, point }) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { makeTaskCompleteCommands } = useMakeCommand();

    const [loading, setLoading] = useState(false);

    const taskId = task.id;
    const pointId = point.id;

    const handleSendGeo = useCallback(async () => {
        let coords: Coords | null = null;

        try {
            setLoading(true);

            coords = await getPreciseLocation();
        } catch (error) {
            console.error(error);

            const geoErr = error as GeolocationPositionError;
            if (geoErr && typeof geoErr === "object" && "code" in geoErr) {
                if (geoErr.code === 1) {
                    notification.error({
                        title: "Не вдалося отримати вашу геолокацію. Дозвольте доступ та спробуйте ще раз.",
                    });

                    return;
                }
            }
        } finally {
            setLoading(false);
        }

        const commands: AnyQueueCommand[] = [];

        if (coords) {
            commands.push(
                makeCommand({
                    type: "postBuildingChangeGeo",
                    title: "Збереження координат торгової точки",
                    params: {
                        buildingId: point.market.building.id,
                        geo: `${coords.lat},${coords.lng}`,
                    },
                }),
            );
        }

        const taskCompleteCommands = await makeTaskCompleteCommands({
            params: {
                taskId,
                hasPhoto: false,
                payload: { result: !!coords, geo: coords },
            },
            coords,
        });

        commands.push(...taskCompleteCommands);

        dispatch(
            enqueueRequestQueueItem({
                id: taskId,
                metadata: { pointId, taskId },
                commands,
            }),
        );

        dispatch(setTaskCompleted({ targetId: pointId, taskId }));

        navigate(ROUTES.WorkPlan, { replace: true });
    }, [
        makeTaskCompleteCommands,
        taskId,
        dispatch,
        pointId,
        navigate,
        notification,
        point.market.building.id,
    ]);

    return (
        <>
            {loading && <Loading title="Завершення задачі" />}

            <Header
                title={task.name}
                back
                rightActions={
                    <SVScanButton pointId={pointId} taskId={taskId} />
                }
            />

            <MainContainer>
                <Flex vertical gap={16}>
                    <PrimaryText fontSize={16}>
                        ТОВ &quot;{point.name}&quot;
                    </PrimaryText>

                    <StyledImageWrapper>
                        <ImageComponent
                            width="100%"
                            height="100%"
                            src={getFileUrl(point.market.building.photo)}
                            fallback={FALLBACK_IMAGE}
                            alt="Point photo"
                        />
                    </StyledImageWrapper>

                    <BaseButton
                        type="primary"
                        icon={<CompassOutlined />}
                        block
                        onClick={handleSendGeo}
                        loading={loading}
                    >
                        Поділитися геолокацією
                    </BaseButton>
                </Flex>
            </MainContainer>
        </>
    );
};
